import React, { FC } from 'react'
import { TitleController, Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  ContextNotification,
  ResidentAppAsideSection,
  ResidentAppAsideText,
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { ParkingSpotsDataLoader } from './ParkingSpotsDataLoader'
import { ParkingArea } from './ParkingArea'
import type {
  CoreParkingArea,
  CoreParkingSpot,
} from '../../../common/validations/contractParkingSpotsSchema'
import { ParkingSpotModal } from './ParkingSpotModal'
import { hasOwnReservation } from './parkingSpots.helpers'
import { isDisabledSpot } from './uiSelectors'
import { ParkingReservationsList } from './ParkingReservationsList'
import { ParkingSpotsLegend } from './ParkingSpotsLegend'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { ReservationRulesAndTermsLinks } from 'components'

export const ParkingSpots: FC = () => {
  const { t } = useTranslation(['common', 'titles', 'parking', 'reservations'])
  const { frontPagePath } = useContractPaths()
  const { parkingSpots } = useModuleConfig()
  const { currentContractId } = useCurrentContract()

  const parkingFallbackLinks: { href: string; label: string }[] = [
    ...(parkingSpots?.contractDocumentLink
      ? [
          {
            href: parkingSpots.contractDocumentLink,
            label: t('reservations:modal.commonAreaRulesLink'),
          },
        ]
      : []),
    ...(parkingSpots?.termsOfUseDocumentLink
      ? [
          {
            href: parkingSpots.termsOfUseDocumentLink,
            label: t('reservations:modal.commonAreaTermsLink'),
          },
        ]
      : []),
  ]

  const [selectedSpot, setSelectedSpot] =
    React.useState<CoreParkingSpot | null>(null)
  const [selectedArea, setSelectedArea] =
    React.useState<CoreParkingArea | null>(null)

  const closeModal = () => {
    setSelectedSpot(null)
    setSelectedArea(null)
  }

  return (
    <TitleController title={t('titles:parking', 'Parking spots')}>
      <ResidentAppView
        title={t('parking:view.title')}
        backLink={
          <ResidentAppBackLink href={frontPagePath}>
            {t('common:views.home')}
          </ResidentAppBackLink>
        }
        aside={
          <ResidentAppAsideSection>
            <ResidentAppAsideText>
              {t('parking:view.description')}{' '}
              {parkingSpots.showQueue && t('parking:view.queueDescription')}
            </ResidentAppAsideText>
            <ReservationRulesAndTermsLinks
              contractId={currentContractId}
              reservableCategory="parking_spot"
              realPropertyHierarchyId={null}
              fallbackLinks={parkingFallbackLinks}
            />
            <React.Suspense fallback={<Loading variant="details" />}>
              <ParkingSpotsDataLoader include="own">
                {({ areas: ownAreas }) => (
                  <ParkingReservationsList areas={ownAreas} />
                )}
              </ParkingSpotsDataLoader>
            </React.Suspense>
          </ResidentAppAsideSection>
        }
      >
        <ResidentAppCard>
          <ParkingSpotsDataLoader include="all">
            {({
              areas,
              limitations,
              queueItems,
              userMetadata,
              reservationPossibleUntil,
            }) => {
              const reservationsEnabled = limitations !== null
              const ownReservationIds = new Set<number>()
              areas.forEach((area) => {
                area.parkingSpots.forEach((spot) => {
                  if (hasOwnReservation(spot)) {
                    ownReservationIds.add(spot.rentableParkingSpotId)
                  }
                })
              })

              const ownReservationCount = ownReservationIds.size
              const perContractSlotLimit =
                limitations?.perContractSlotLimit ?? null
              const complimentarySlotsPerContract =
                limitations?.complimentarySlotsPerContract ?? 0
              const complimentarySlotsRemaining = Math.max(
                0,
                complimentarySlotsPerContract - ownReservationCount
              )
              const canReserveDisabledSpots =
                limitations?.canTenantReserveSlotsForDisabled ?? true

              const hasDisabledSpots = areas.some((area) =>
                area.parkingSpots.some(isDisabledSpot)
              )
              const hasReachedLimit =
                reservationsEnabled &&
                typeof perContractSlotLimit === 'number' &&
                perContractSlotLimit >= 0 &&
                ownReservationCount >= perContractSlotLimit

              return !reservationsEnabled ? (
                <Alert variant="info" className="my-4">
                  {t('parking:view.reservationsNotSet')}
                </Alert>
              ) : (
                <div className="flex flex-col gap-6">
                  {reservationsEnabled && complimentarySlotsRemaining > 0 && (
                    <Alert variant="info">
                      {t('parking:view.complimentaryInfo', {
                        count: complimentarySlotsRemaining,
                      })}
                    </Alert>
                  )}

                  {reservationsEnabled && hasReachedLimit && (
                    <Alert variant="warning">
                      {t('parking:view.limitReached', {
                        max: perContractSlotLimit ?? 0,
                      })}
                    </Alert>
                  )}
                  {areas.map((area) => (
                    <ParkingArea
                      key={
                        area.location.locationDetails ??
                        `${area.location.address.streetName}-${area.location.address.streetNumber}-${area.location.address.city}-${area.costPoolId}`
                      }
                      area={area}
                      canReserveDisabledSpots={canReserveDisabledSpots}
                      reservationsEnabled={reservationsEnabled}
                      onOpenSpot={(spot) => {
                        // Never open the parking spot modal for impersonators.
                        if (userMetadata.isImpersonator) return

                        setSelectedSpot(spot)
                        setSelectedArea(area)
                      }}
                      queueItems={queueItems.filter(
                        (item) => item.costPoolId === area.costPoolId
                      )}
                      userMetadata={userMetadata}
                    />
                  ))}
                  {areas.length === 0 && (
                    <Alert variant="info" className="my-8">
                      {t('parking:view.noParkingAreas')}
                    </Alert>
                  )}
                  <ParkingSpotModal
                    spot={selectedSpot}
                    area={selectedArea}
                    reservationPossibleUntil={reservationPossibleUntil}
                    onClose={closeModal}
                    hasReachedLimit={hasReachedLimit}
                    limit={perContractSlotLimit}
                    canReserveDisabledSpots={canReserveDisabledSpots}
                    complimentarySlotsRemaining={complimentarySlotsRemaining}
                    reservationsEnabled={reservationsEnabled}
                  />
                  {reservationsEnabled &&
                    !canReserveDisabledSpots &&
                    hasDisabledSpots && (
                      <ContextNotification type="info" icon="inva">
                        {t('parking:view.disabledReservationInfo')}
                      </ContextNotification>
                    )}

                  <ParkingSpotsLegend areas={areas} />
                </div>
              )
            }}
          </ParkingSpotsDataLoader>
        </ResidentAppCard>
      </ResidentAppView>
    </TitleController>
  )
}
